// docs/38 BS6/BS7 — the free-form field's edge, for the board layers. A shaped fight's board is
// the bounding rectangle of its field; the squares outside the field are `Void` (no board). The
// sim already treats them as off the board; here the view finds the edge they make: where the
// void layer covers and rims, and how far a point is from it (so particles fade out at the field's
// own edge instead of drifting over the backdrop).
import { onBoard, type Terrain, type Tile } from '../../core/sim/types';

export interface FieldShape { w: number; h: number; tiles: ReadonlyArray<{ terrain: Terrain } | undefined> }

/** Whether the board has any void square (a rectangle board draws exactly as before). */
export function isShaped(board: FieldShape): boolean {
  return board.tiles.some((t) => t?.terrain === 'Void');
}

export type Side = 'n' | 'e' | 's' | 'w';
const SIDES: ReadonlyArray<readonly [Side, number, number]> = [['n', 0, -1], ['e', 1, 0], ['s', 0, 1], ['w', -1, 0]];

export interface FieldEdges {
  /** Void squares inside the rectangle: painted over with the backdrop. */
  cover: Tile[];
  /** Field squares' sides that face no board (void or off the rectangle): the soft dark rim. */
  rims: Array<Tile & { side: Side }>;
  /** Squares just south of the field with no board (void or below the rectangle): the bank's face. */
  banks: Tile[];
}

/** The edge of a shaped field; empty for a rectangle board (its edge is the backdrop's). */
export function fieldEdges(board: FieldShape): FieldEdges {
  const out: FieldEdges = { cover: [], rims: [], banks: [] };
  if (!isShaped(board)) return out;
  for (let y = 0; y < board.h; y++) {
    for (let x = 0; x < board.w; x++) {
      const t = { x, y };
      if (!onBoard(board, t)) { out.cover.push(t); continue; }
      for (const [side, dx, dy] of SIDES) {
        const n = { x: x + dx, y: y + dy };
        if (onBoard(board, n)) continue;
        out.rims.push({ x, y, side });
        if (side === 's') out.banks.push(n);
      }
    }
  }
  return out;
}

export interface Rect { x: number; y: number; w: number; h: number }

/**
 * Squares as the fewest plain rectangles (cells): each row's runs of neighbouring squares, and a
 * run carried down while the rows below have the very same run (`down: false` keeps every rect
 * one row tall, for marks that cover only part of a square's height). A Graphics redraws every
 * command every frame, so the void layer draws a crawl's thousands of squares as a few hundred rects.
 */
export function mergeRuns(tiles: ReadonlyArray<Tile>, down = true): Rect[] {
  const rows = new Map<number, number[]>();
  for (const t of tiles) { const r = rows.get(t.y); if (r) r.push(t.x); else rows.set(t.y, [t.x]); }
  const open = new Map<string, Rect>();
  const out: Rect[] = [];
  for (const y of [...rows.keys()].sort((a, b) => a - b)) {
    const xs = [...new Set(rows.get(y))].sort((a, b) => a - b);
    const next = new Map<string, Rect>();
    for (let i = 0; i < xs.length;) {
      let j = i;
      while (j + 1 < xs.length && xs[j + 1] === xs[j]! + 1) j++;
      const x = xs[i]!, w = xs[j]! - x + 1, key = `${x},${w}`;
      const above = down ? open.get(key) : undefined;
      if (above && above.y + above.h === y) { above.h++; next.set(key, above); } else { const r = { x, y, w, h: 1 }; out.push(r); next.set(key, r); }
      i = j + 1;
    }
    open.clear();
    for (const [k, r] of next) open.set(k, r);
  }
  return out;
}

/** The last distance field made, by the board's shape (fields are only read). */
let lastFade: { key: string; dist: Float32Array } | undefined;

/** Samples per cell side in the precomputed distance field (4 art px at 24 px cells). */
const SUB = 6;
/** How far (cells) the distance field looks for the edge; fades never reach further. */
const REACH = 3;

/**
 * `(x, y, r) → 0..1`: how far a board-space point is inside the field, over `r` px — 0 on or past
 * the edge (a void square, or outside the rectangle), 1 at `r` or more from it. The particle
 * fades (`inside`) of the lighting, weather and fog layers. A rectangle board is the plain
 * distance to its sides, exactly as before.
 */
export function fieldFade(board: FieldShape, cell: number): (x: number, y: number, r: number) => number {
  const W = board.w * cell, H = board.h * cell;
  const rect = (x: number, y: number): number => Math.min(x, W - x, y, H - y);
  if (!isShaped(board)) return (x, y, r) => Math.max(0, Math.min(1, rect(x, y) / r));
  const gw = board.w * SUB, gh = board.h * SUB, step = cell / SUB;
  const isVoid = new Uint8Array(board.w * board.h);
  for (let y = 0; y < board.h; y++) for (let x = 0; x < board.w; x++) if (!onBoard(board, { x, y })) isVoid[y * board.w + x] = 1;
  // the lighting and the weather each ask for the same board's field on every relight: keep the last
  const key = `${board.w}x${board.h}@${cell}:${isVoid.join('')}`;
  let dist = lastFade?.key === key ? lastFade.dist : undefined;
  if (!dist) {
    dist = new Float32Array(gw * gh).fill(REACH * cell);
    // the void squares within REACH of each square, looked up on the board (not a scan of every
    // void square per sample: a crawl has thousands, which froze the page for seconds on every relight)
    for (let j = 0; j < gh; j++) {
      const cy = Math.floor(j / SUB), py = (j + 0.5) * step;
      for (let i = 0; i < gw; i++) {
        const cx = Math.floor(i / SUB);
        // a sample on a void square is on it: no distance at all
        if (isVoid[cy * board.w + cx]) { dist[j * gw + i] = 0; continue; }
        const px = (i + 0.5) * step;
        const d0 = dist[j * gw + i]!;
        let d2 = d0 * d0;
        for (let vy = Math.max(0, cy - REACH); vy <= Math.min(board.h - 1, cy + REACH); vy++) {
          for (let vx = Math.max(0, cx - REACH); vx <= Math.min(board.w - 1, cx + REACH); vx++) {
            if (!isVoid[vy * board.w + vx]) continue;
            const dx = Math.max(0, vx * cell - px, px - (vx + 1) * cell);
            const dy = Math.max(0, vy * cell - py, py - (vy + 1) * cell);
            d2 = Math.min(d2, dx * dx + dy * dy);
          }
        }
        dist[j * gw + i] = Math.sqrt(d2);
      }
    }
    lastFade = { key, dist };
  }
  const field = dist;
  return (x, y, r) => {
    const i = Math.floor(x / step), j = Math.floor(y / step);
    if (i < 0 || j < 0 || i >= gw || j >= gh) return 0;
    return Math.max(0, Math.min(1, Math.min(rect(x, y), field[j * gw + i]!) / r));
  };
}
